import { supabase } from '@/services/supabase';
import type { DojoPracticeActivity } from '@/types/dojo';

export const FOLLOW_THROUGH_CHOICES = [
  { value: 'tried', label: 'I tried it' },
  { value: 'noticed', label: 'I noticed it' },
  { value: 'not-yet', label: 'It didn’t arise' },
] as const;
export type FollowThroughChoice = typeof FOLLOW_THROUGH_CHOICES[number]['value'];
export const FOLLOW_THROUGH_NOTE_MAX = 600;
export interface PracticeFollowThrough {
  round_id: string;
  practice_title: string;
  response: FollowThroughChoice;
  reflection_day: string;
  created_at: string;
  /** Preview only; requires a separate reviewed database change before rollout. */
  note?: string;
}

export function reflectionDay(date = new Date()): string {
  return [date.getFullYear(), String(date.getMonth() + 1).padStart(2, '0'),
    String(date.getDate()).padStart(2, '0')].join('-');
}

const PREVIEW_KEY = 'howie-follow-through-preview-v1';
const columns = 'round_id,practice_title,response,reflection_day,created_at';
export const isFollowThroughPreview = (preview: boolean) => import.meta.env.DEV && preview;
// Developer handoff: apply the follow-through migration before enabling this flag.
// See apps/howie/docs/follow-through-handoff.md.
export const isFollowThroughEnabled = () => import.meta.env.VITE_HOWIE_FOLLOW_THROUGH_ENABLED === 'true';

function isFollowThrough(row: unknown): row is PracticeFollowThrough {
  if (typeof row !== 'object' || row === null || Array.isArray(row)) return false;
  const value = row as Record<string, unknown>;
  if (typeof value.reflection_day !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(value.reflection_day)) return false;
  const day = new Date(value.reflection_day + 'T00:00:00Z');
  return typeof value.round_id === 'string' && value.round_id.length > 0
    && typeof value.practice_title === 'string' && value.practice_title.length > 0 && value.practice_title.length <= 300
    && Number.isFinite(day.getTime()) && day.toISOString().slice(0, 10) === value.reflection_day
    && FOLLOW_THROUGH_CHOICES.some((choice) => choice.value === value.response)
    && typeof value.created_at === 'string' && Number.isFinite(Date.parse(value.created_at))
    && (value.note === undefined || (typeof value.note === 'string' && value.note.length <= FOLLOW_THROUGH_NOTE_MAX));
}

function followThroughHistory(value: unknown): PracticeFollowThrough[] {
  if (!Array.isArray(value) || !value.every(isFollowThrough)) {
    throw new Error('Invalid reflection history');
  }
  return value;
}

function previewHistory(activity: DojoPracticeActivity[]): PracticeFollowThrough[] {
  const stored = sessionStorage.getItem(PREVIEW_KEY);
  if (stored !== null) {
    let parsed: unknown;
    try {
      parsed = JSON.parse(stored);
    } catch {
      throw new Error('Invalid reflection history');
    }
    return followThroughHistory(parsed);
  }
  const earlier = activity[activity.length - 1];
  if (!earlier) return [];
  return FOLLOW_THROUGH_CHOICES.map((choice, index) => {
    const date = new Date(earlier.completed_at);
    date.setDate(date.getDate() + index + 1);
    return {
      round_id: earlier.round_id, practice_title: earlier.title,
      response: choice.value, reflection_day: reflectionDay(date),
      created_at: date.toISOString(),
    };
  }).sort((a, b) => b.reflection_day.localeCompare(a.reflection_day));
}

export async function loadFollowThrough(
  userId: string | undefined, preview: boolean, activity: DojoPracticeActivity[]
): Promise<PracticeFollowThrough[]> {
  if (isFollowThroughPreview(preview)) return previewHistory(activity);
  if (!isFollowThroughEnabled()) return [];
  if (!userId) return [];
  const { data, error } = await supabase.from('howie_practice_follow_through')
    .select(columns).eq('user_id', userId)
    .order('reflection_day', { ascending: false }).order('created_at', { ascending: false }).limit(100);
  if (error) throw error;
  return followThroughHistory(data);
}

export async function saveFollowThrough(
  userId: string | undefined, preview: boolean, activity: DojoPracticeActivity[],
  practice: DojoPracticeActivity, response: FollowThroughChoice, note?: string
): Promise<PracticeFollowThrough> {
  const row = {
    round_id: practice.round_id, practice_title: practice.title.slice(0, 300),
    response, reflection_day: reflectionDay(),
  };
  if (isFollowThroughPreview(preview)) {
    if (note !== undefined && note.length > FOLLOW_THROUGH_NOTE_MAX) throw new Error('Note is too long');
    const history = previewHistory(activity);
    const existing = history.find((item) =>
      item.round_id === row.round_id && item.reflection_day === row.reflection_day);
    const saved = { ...row, note: note === undefined ? existing?.note : note.trim(),
      created_at: existing?.created_at ?? new Date().toISOString() };
    sessionStorage.setItem(PREVIEW_KEY, JSON.stringify([saved, ...history.filter((item) =>
      item.round_id !== row.round_id || item.reflection_day !== row.reflection_day)]));
    return saved;
  }
  if (note !== undefined) throw new Error('Reflection notes are preview-only');
  if (!isFollowThroughEnabled()) throw new Error('Reflection saving is not enabled');
  if (!userId) throw new Error('Sign in to save a reflection');
  const { data, error } = await supabase.from('howie_practice_follow_through')
    .upsert({ ...row, user_id: userId }, { onConflict: 'user_id,round_id,reflection_day' })
    .select(columns).single();
  if (error) throw error;
  if (!isFollowThrough(data)) throw new Error('Invalid reflection history');
  return data;
}
